import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import get from 'lodash/get';
import { setFilter } from 'nallian-shared-ui/lib/hoc/redux-filters/actions';
import { Dropdown, Header, Divider, Flex, Icon } from 'nallian-shared-ui/lib/components';
import { FiltersSegment } from '../../../../app/styles';
import appSubscriptionsService from '../../../services/appSubscriptions/appSubscriptionsService';

const PrincipalFilterDropdown = styled(Dropdown)`
  && {
    display: inline-flex;
    align-items: center;
  }

  && .close {
    position: relative;
    top: 0.05rem;
    left: 0.2rem;
  }
`;

const PrincipalFilters = ({
  resource,
  organisationId,
  dispatchAppSubscriptionsGet,
  appSubscriptionsFetch,
}) => {
  const [filterName, setFilterName] = useState();
  const { t } = useTranslation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (organisationId) dispatchAppSubscriptionsGet(organisationId);
  }, [organisationId, dispatchAppSubscriptionsGet]);

  const handleSetFilter = useCallback(
    (_, { value }) => {
      const split = value?.split('_');
      const name = get(split, '[0]');
      const roleId = get(split, '[1]');
      setFilterName(name);
      dispatch(setFilter(`${resource}OverviewFilters`, 'roleId', roleId));
    },
    [resource, setFilterName, dispatch]
  );

  const handleClearFilter = useCallback(() => {
    setFilterName(undefined);
    dispatch(setFilter(`${resource}OverviewFilters`, 'roleId', undefined));
  }, [resource, setFilterName, dispatch]);

  const apps = useMemo(
    () =>
      (appSubscriptionsFetch.value || []).map(a => ({
        key: `${a.appInstallation.tag}_${a.community.tag}`,
        value: `${a.appInstallation.tag}_${a.community.tag}`,
        text: `${a.alias || a.appInstallation?.name} (${a.community?.name})`,
        features: (a.communityAppFeatures || []).map(caf => ({
          key: caf.appInstallationFeature?.tag,
          value: caf.appInstallationFeature?.tag,
          text: caf.alias || caf.appInstallationFeature?.name,
          roles: (caf.communityAppFeatureRoles || []).map(cafr => ({
            key: `${cafr.alias || cafr.appInstallationFeatureRole?.name}_${cafr?.id}`,
            value: `${cafr.alias || cafr.appInstallationFeatureRole?.name}_${cafr?.id}`,
            text: cafr.alias || cafr.appInstallationFeatureRole?.name,
          })),
        })),
      })),
    [appSubscriptionsFetch.value]
  );

  return (
    <FiltersSegment secondary>
      <Flex fluid margin="0 0 0.5rem">
        <Header as="h4">{t('filters')}</Header>
      </Flex>
      <Divider />
      <PrincipalFilterDropdown
        simple
        text={
          filterName ? (
            <Flex fluid alignItems="center">
              {filterName}
              <Icon name="close" onClick={handleClearFilter} iconbutton />
            </Flex>
          ) : (
            t('filterOnUserRole')
          )
        }
      >
        <Dropdown.Menu fluid>
          <Dropdown.Header fluid>{t('apps_plural')}</Dropdown.Header>

          {apps.map(a => (
            <Dropdown.Item key={a.value} fluid>
              <Dropdown simple pointing="left" text={a.text} fluid>
                <Dropdown.Menu fluid>
                  <Dropdown.Header fluid>{t('features')}</Dropdown.Header>
                  {a.features.map(f => (
                    <Dropdown.Item key={f.value} fluid>
                      <Dropdown simple pointing="left" text={f.text} fluid>
                        <Dropdown.Menu>
                          <Dropdown.Header>{t('roles')}</Dropdown.Header>
                          {f.roles.map(r => (
                            <Dropdown.Item
                              onClick={handleSetFilter}
                              key={r.value}
                              value={r.value}
                              fluid
                            >
                              {r.text}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </PrincipalFilterDropdown>
    </FiltersSegment>
  );
};

PrincipalFilters.propTypes = {
  organisationId: PropTypes.string.isRequired,
  dispatchAppSubscriptionsGet: PropTypes.func.isRequired,
  appSubscriptionsFetch: PropTypes.object.isRequired,
  resource: PropTypes.string.isRequired,
};

export default appSubscriptionsService(PrincipalFilters);
